import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { MapPin, Shield, Heart } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { listingUrl } from "@/lib/slug";

export interface ScooterListing {
  id: string;
  title: string;
  price: number;
  condition: "nuevo" | "usado" | "recambios";
  brand: string;
  model: string;
  city: string;
  image: string;
  dgt_approved: boolean;
  created_at: string;
}

const conditionColors: Record<string, string> = {
  nuevo: "bg-electric text-electric-foreground",
  usado: "bg-accent text-accent-foreground",
  recambios: "bg-muted text-muted-foreground",
};

const conditionLabels: Record<string, string> = {
  nuevo: "Nuevo",
  usado: "Usado",
  recambios: "Recambios",
};

/**
 * Request a smaller variant from known image CDNs to avoid serving multi-MP
 * source files for ~280px card thumbnails. Falls back to the original URL.
 */
const optimizeCardImage = (src: string): string => {
  if (!src) return src;
  try {
    const u = new URL(src, window.location.origin);
    const host = u.hostname;
    // Sanity image pipeline
    if (host.endsWith("sanity.io")) {
      u.searchParams.set("w", "600");
      u.searchParams.set("q", "75");
      u.searchParams.set("auto", "format");
      u.searchParams.set("fit", "max");
      return u.toString();
    }
    // Xiaomi appmifile already serves WebP; cap width
    if (host.endsWith("appmifile.com")) {
      u.searchParams.set("w", "600");
      return u.toString();
    }
    // Shopify CDN supports `_600x` filename suffix or width param
    if (host.endsWith("shopify.com") || host.endsWith("cdn.shopify.com")) {
      u.searchParams.set("width", "600");
      return u.toString();
    }
    return src;
  } catch {
    return src;
  }
};

const ScooterCard = ({ listing, distanceKm }: { listing: ScooterListing; distanceKm?: number }) => {
  const { user } = useAuth();
  const [isFav, setIsFav] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("favorites")
      .select("id")
      .eq("user_id", user.id)
      .eq("listing_id", listing.id)
      .maybeSingle()
      .then(({ data }) => setIsFav(!!data));
  }, [user, listing.id]);

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Inicia sesión para guardar favoritos");
      return;
    }
    setLoading(true);
    if (isFav) {
      await supabase.from("favorites").delete().eq("user_id", user.id).eq("listing_id", listing.id);
      setIsFav(false);
      toast.success("Eliminado de favoritos");
    } else {
      await supabase.from("favorites").insert({ user_id: user.id, listing_id: listing.id });
      setIsFav(true);
      toast.success("Añadido a favoritos");
    }
    setLoading(false);
  };

  return (
    <Link
      to={listingUrl(listing)}
      className="group overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/10"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <img
          src={optimizeCardImage(listing.image)}
          alt={listing.title}
          width={600}
          height={450}
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 280px"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute left-3 top-3 flex gap-2">
          <Badge className={conditionColors[listing.condition]}>
            {conditionLabels[listing.condition]}
          </Badge>
          {listing.dgt_approved && (
            <Badge className="bg-secondary text-secondary-foreground">
              <Shield className="mr-1 h-3 w-3" /> DGT
            </Badge>
          )}
        </div>
        <button
          onClick={toggleFavorite}
          disabled={loading}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-background/80 backdrop-blur-sm transition-colors hover:bg-background"
          aria-label={isFav ? "Quitar de favoritos" : "Añadir a favoritos"}
        >
          <Heart className={`h-4 w-4 transition-colors ${isFav ? "fill-destructive text-destructive" : "text-muted-foreground"}`} />
        </button>
      </div>
      <div className="p-4">
        <p className="text-xs text-muted-foreground">{listing.brand} · {listing.model}</p>
        <h3 className="mt-1 font-display font-semibold leading-tight line-clamp-2">{listing.title}</h3>
        <div className="mt-3 flex items-end justify-between">
          <span className="font-display text-xl font-bold text-primary">{listing.price.toLocaleString("es-ES")} €</span>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3" /> {listing.city}
          </span>
        </div>
        {typeof distanceKm === "number" && (
          <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
            <MapPin className="h-3 w-3" />
            a {distanceKm < 1 ? "<1" : Math.round(distanceKm)} km de ti
          </p>
        )}
      </div>
    </Link>
  );
};

export default ScooterCard;
